import { Inject, Injectable } from '@angular/core';
import { Observable, of, EMPTY } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { CommonHttpClientService } from '../share/service/common-http-client.service';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../web-generator/generator-env-token';
import { ITRISearchFilterParam } from './entity/itri-filter-search-param';
import { SearchForm } from './entity/search-form';
import { SearchOptions } from './entity/search-options';
import { ITRISearchResult } from './entity/search-result';

@Injectable()
export class DataSearchService {
  constructor(
    protected commonHttp: CommonHttpClientService,
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
  ) { }

  /**
   * 走 ITRI 關鍵字搜尋撈結果
   * @param params 分頁紀錄的 params
   */
   searchByITRI(params: { limit: number; offset: number; sort: string; order: string; keyword: string; isFuzzyMode: boolean; query: string; }): Observable<ITRISearchResult> {
    const filters: ITRISearchFilterParam = {};
    let negKeyword: string;
    if (params.query) {
      const query = JSON.parse(params.query) as SearchForm;
      if (query.subject) {
        filters['subject'] = query.subject.data.key;
      }
      if (query.format) {
        filters['format'] = query.format.data.key;
      }
      if (query.fetchIndex) {
        filters['fetchIndex'] = query.fetchIndex.map(opt => opt.data.key).join('##');
      }
      if (query.rights) {
        filters['rights'] = query.rights.map(opt => opt.data.key).join('##');
      }
      if (query.desRights) {
        filters['desRights'] = query.desRights.map(opt => opt.data.key).join('##');
      }
      if (query.city) {
        filters['dist'] = `${query.city.label}##${query.dist ? query.dist.label : ''}`;
      }
      if (query.startYear && query.endYear) {
        filters['dateRange'] = `${query.startYear}-01-01##${query.endYear}-12-31`;
      }
      if (query.negKeyword) {
        negKeyword = query.negKeyword;
      }
    }

    const body = new URLSearchParams();
    body.set('paras', JSON.stringify({
      keyword: params.keyword,
      sort: params.sort,
      order: params.order,
      filter: filters,
      // {
      //   // 'dateRange': '1905-05-01##2000-01-01',
      //   // 'subject': '產業與經濟',
      //   'format': '圖像',
      //   // 'fetchIndex': 'Culture_Event',
      //   // 'rights': 'CC BY-NC##PDM',
      //   // 'desRights': 'CC BY',
      //   // 'dist': '苗栗縣##臺東縣',
      //   // 'century': '1900~1999'
      // },
      start: `${params.offset}`,
      rows: `${params.limit}`,
      fuzzyMode: params.isFuzzyMode ? '1' : '0',
      negKeyword,
    }));

    return this.commonHttp.post<ITRISearchResult>(`${this.generatorEnv.itriSearchApiHost}/v2/search/text`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    }).pipe(
      catchError(err => of(null)),
    );
  }

  /**
   * 我們的取列表資料 API
   * @param params 分頁紀錄的 params
   */
  searchByMore(params: { limit: number; offset: number; sort: string; order: string; keyword: string; isFuzzyMode: boolean; query: string; }): Observable<ITRISearchResult> {
    const moreQuery = this.genMoreQueryParams(params);

    // return timer(500).pipe(map(t => {
    //   return {
    //     totalSize: 0,
    //     records: [],
    //     groupBy: []
    //   } as any as ITRISearchResult;
    // }));
    return this.commonHttp.get<ITRISearchResult>(`${this.generatorEnv.apiHost}/api/collection`, { params: moreQuery}).pipe(
      catchError(err => of(null)),
    );
  }

  /**
   * 取得參數鍵值對應表
   * @param params 分頁紀錄的 params
   */
  getParams(params?: { limit: number; offset: number; sort: string; order: string; keyword: string; isFuzzyMode: boolean; query: string; }, isReFetch = false): Observable<SearchOptions> {
    let moreQuery: any = {};
    if (params) {
      moreQuery = this.genMoreQueryParams(params);
    }
    return this.commonHttp.get<SearchOptions>(`${this.generatorEnv.apiHost}/api/collection/params`, { params: moreQuery}).pipe(
      tap(v => {
        // 去除掉未定義的選項
        if (v.rights) {
          v.rights = v.rights.filter(opt => opt.id >= 0);
        }
        if (v.desRights) {
          v.desRights = v.desRights.filter(opt => opt.id >= 0);
        }
      }),
    );
  }

  /**
   * 紀錄使用者查詢關鍵字後點了某筆素材的行為
   * @param fetchIndex 資料來源機關
   * @param materialId 素材編號
   * @param keyword 查詢用的關鍵字
   * @param queryTime 查詢當下的時間戳記(2022-10-26T07:02:41.635Z)
   */
  logUser2DetailWithKeyword(fetchIndex: string, materialId: string, keyword: string, queryTime: string): Observable<void> {
    return this.commonHttp.post<void>(`${this.generatorEnv.apiHost}/api/collection/query/logger`, {
      sourceType: fetchIndex,
      uid: materialId,
      keyword,
      queryTime,
    });
  }

  /** 轉換頁面的查詢參數城我們 API 的參數格式 */
  private genMoreQueryParams(params: { limit: number; offset: number; sort: string; order: string; keyword: string; isFuzzyMode: boolean; query: string; }) {
    const moreQuery: {
      format?: number;
      imageLic?: number[];
      contentLic?: number[];
      subject?: number;
      source?: number[];
      dept?: string;
      city?: number;
      dist?: number;
      startYear?: number;
      endYear?: number;
      page: number;
      size: number;
      sort: string[];
    } = {
      page: Math.floor(params.offset / params.limit),
      size: params.limit,
      sort: [params.sort + ',' + params.order],
    };

    if (params.query) {
      const query = JSON.parse(params.query) as SearchForm;
      if (query.subject) {
        moreQuery.subject = query.subject.id;
      }
      if (query.format) {
        moreQuery.format = query.format.id;
      }
      if (query.fetchIndex) {
        moreQuery.source = query.fetchIndex.map(opt => opt.id);
      }
      if (query.rights) {
        moreQuery.imageLic = query.rights.map(opt => opt.id);
      }
      if (query.desRights) {
        moreQuery.contentLic = query.desRights.map(opt => opt.id);
      }
      if (query.city) {
        moreQuery.city = query.city.id;
      }
      if (query.dist) {
        moreQuery.dist = query.dist.id;
      }
      if (query.startYear) {
        moreQuery.startYear = +query.startYear;
      }
      if (query.endYear) {
        moreQuery.endYear = +query.endYear;
      }
      if (query.createDept) {
        moreQuery.dept = query.createDept;
      }
    }

    return moreQuery;
  }
}
